import CopyButton from "@components/buttons/CopyButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import { emailLogsService } from "@services/apis/EmailLogs";
import {
  EmailLog,
  EmailLogEvent,
} from "@services/models/EmailLogs/EmailLogsModel";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ConstantFunctions from "../../../utils/constants_functions";
import EmailLogStatusPill, {
  getEmailLogStatusColors,
} from "./EmailLogStatusPill";

interface EmailLogDetailsProps {
  emailLogId: string;
}

const constantFunctions = new ConstantFunctions();

const formatDateTime = (value?: string | null) => {
  if (!value) return "- -";
  try {
    return constantFunctions.formatDateTime(value);
  } catch {
    return value;
  }
};

const formatJson = (value: unknown) => {
  if (value === undefined || value === null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const eventLabel = (event?: string) => {
  if (!event) return "Unknown";
  return event
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const TruncatedId: React.FC<{ value?: string | null; label: string }> = ({
  value,
  label,
}) => {
  if (!value) {
    return (
      <span className="text-[12px] font-normal text-text-base">- -</span>
    );
  }

  return (
    <div className="flex max-w-[180px] flex-row items-start gap-[4px]">
      <span className="line-clamp-2 break-all text-right text-[12px] font-normal leading-tight text-text-base">
        {value}
      </span>
      <CopyButton
        copyText={value}
        copyMessage={`${label} copied to clipboard`}
        copyIconSize={10}
        buttonSize="16px"
      />
    </div>
  );
};

const EmailLogDetails: React.FC<EmailLogDetailsProps> = ({ emailLogId }) => {
  const [emailLog, setEmailLog] = useState<EmailLog | null>(null);
  const [events, setEvents] = useState<EmailLogEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showTimeline, setShowTimeline] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError(false);
        const res = await emailLogsService.getLogDetails(emailLogId);
        if (cancelled) return;
        setEmailLog(res.data?.emailLog ?? null);
        setEvents(res.data?.events ?? []);
      } catch {
        if (cancelled) return;
        setEmailLog(null);
        setEvents([]);
        setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetails();

    return () => {
      cancelled = true;
    };
  }, [emailLogId]);

  if (error) {
    return <div className="mt-[20px] text-[12px]">Fetch error</div>;
  }

  if (loading) {
    return (
      <div className="mt-[20px] flex flex-col gap-3">
        <Skeleton height={80} />
        <Skeleton height={180} />
        <Skeleton height={120} />
      </div>
    );
  }

  if (!emailLog) {
    return (
      <div className="mt-[20px] text-[12px] text-text-subtle">
        No email log found
      </div>
    );
  }

  const templateDataJson = formatJson(emailLog.templateData);
  const recipientsJson = formatJson(emailLog.recipientsJson);

  return (
    <div className="mt-[20px] flex flex-col gap-[12px]">
      <div className="flex flex-col space-y-[12px] rounded-lg border border-border-subtle bg-bg-base px-[12px] py-[8px]">
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex min-w-0 flex-row items-center gap-[8px] rounded bg-bg-page-body px-[12px] py-[4px]">
            <p className="max-w-[140px] truncate text-[10px] font-bold text-text-base">
              Log ID: {emailLog.emailLogId}
            </p>
            <CopyButton
              copyText={emailLog.emailLogId}
              copyIconSize={12}
              buttonSize="16px"
              copyMessage="Email log ID copied to clipboard"
            />
          </div>
          <EmailLogStatusPill status={emailLog.status} />
        </div>

        <div className="flex flex-col">
          <div
            className="flex w-full cursor-pointer flex-row items-center justify-between rounded-[4px] border border-border-subtle px-3 py-2 hover:bg-bg-subtle"
            onClick={() => setShowTimeline((prev) => !prev)}
          >
            <span className="text-[12px] font-medium text-text-base">
              Event Timeline
            </span>
            <span className="material-symbols-outlined text-[18px] text-text-secondary">
              {showTimeline ? "keyboard_arrow_up" : "keyboard_arrow_down"}
            </span>
          </div>

          {showTimeline && events.length > 0 && (
            <div className="mt-3 px-4 py-2">
              <div className="relative">
                {events.map((event, index) => {
                  const isLast = index === events.length - 1;
                  const colors = getEmailLogStatusColors(
                    event.event?.toUpperCase(),
                  );

                  return (
                    <div
                      key={event.id}
                      className="relative flex min-h-[56px] gap-3"
                    >
                      {!isLast && (
                        <div className="absolute bottom-[-8px] left-[7px] top-[16px] w-[2px] bg-border-subtle" />
                      )}
                      <div className="relative z-[1] flex-shrink-0">
                        <div
                          className={`flex h-[16px] w-[16px] items-center justify-center rounded-full border-2 ${colors.bg}`}
                          style={{ borderColor: colors.text }}
                        />
                      </div>
                      <div className="-mt-[2px] flex flex-col pb-4">
                        <span className="text-[11px] font-semibold text-text-base">
                          {eventLabel(event.event)}
                          {isLast && (
                            <span className="ml-2 text-[9px] font-medium text-text-navbar-active">
                              CURRENT
                            </span>
                          )}
                        </span>
                        <span className="mt-[2px] text-[10px] text-text-secondary">
                          {formatDateTime(event.eventTimestamp)}
                        </span>
                        {event.response && (
                          <span className="mt-[2px] break-all text-[10px] text-text-subtle">
                            {event.response}
                          </span>
                        )}
                        {(event.ip || event.userAgent) && (
                          <span className="mt-[2px] line-clamp-2 break-all text-[10px] text-text-subtle">
                            {[event.ip, event.userAgent]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {showTimeline && events.length === 0 && (
            <div className="px-4 py-3 text-[10px] text-text-secondary">
              No event history available.
            </div>
          )}
        </div>

        <LabelRow label="Subject" value={emailLog.subject || "- -"} />
        <LabelRow label="Recipient" value={emailLog.recipient} isCopy />
        {emailLog.recipientCount != null && (
          <LabelRow
            label="Recipient Count"
            value={String(emailLog.recipientCount)}
          />
        )}
        <LabelRow label="From" value={emailLog.fromEmail || "- -"} />
        <LabelRow label="From Name" value={emailLog.fromName || "- -"} />
        <LabelRow label="Template" value={emailLog.templateName || "- -"} />
        <LabelRow
          label="Template ID"
          value={emailLog.templateId || "- -"}
          isCopy={Boolean(emailLog.templateId)}
        />
        <LabelRow label="Provider" value={emailLog.provider || "- -"} />
        <LabelRow
          label="SendGrid Message ID"
          valueComp={
            <TruncatedId
              value={emailLog.sendgridMessageId}
              label="SendGrid Message ID"
            />
          }
        />
        <LabelRow
          label="Status Code"
          value={
            emailLog.statusCode != null ? String(emailLog.statusCode) : "- -"
          }
        />
        <LabelRow
          label="Reference"
          value={
            [emailLog.referenceType, emailLog.referenceId]
              .filter(Boolean)
              .join(" · ") || "- -"
          }
        />
        <LabelRow
          label="Created At"
          value={formatDateTime(emailLog.createdAt)}
        />
        <LabelRow label="Sent At" value={formatDateTime(emailLog.sentAt)} />
        <LabelRow
          label="Delivered At"
          value={formatDateTime(emailLog.deliveredAt)}
        />
        <LabelRow
          label="Opened At"
          value={formatDateTime(emailLog.openedAt)}
        />
      </div>

      {recipientsJson && (
        <JsonBlock title="Recipients" json={recipientsJson} />
      )}
      {templateDataJson && (
        <JsonBlock title="Template Data" json={templateDataJson} />
      )}
    </div>
  );
};

const JsonBlock: React.FC<{ title: string; json: string }> = ({
  title,
  json,
}) => (
  <div className="rounded-lg border border-border-subtle bg-bg-base">
    <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
      <p className="text-[12px] font-medium text-text-base">{title}</p>
      <CopyButton
        copyText={json}
        copyMessage={`${title} copied!`}
        copyIconSize={10}
        buttonSize="18px"
      />
    </div>
    <pre className="max-h-[180px] overflow-auto whitespace-pre-wrap break-all p-3 text-[10px] text-text-base">
      {json}
    </pre>
  </div>
);

export default EmailLogDetails;
